let tokenCache = {
    token: null,
    expiresAt: null,
};


/**
 * Wraps an asp.net validation problem details errors array.
 */
export function ValidationError(errors) {
    this.errors = errors;
    return this;
}

export function ApiError(response) {
    this.response = response;
    return this;
}

async function handleResponse(response, returnAsJson = true) {
    if (response.ok) {
        const contentLength = response.headers.get('content-length');
        if (contentLength && contentLength === '0') {
            return null;
        }
        return returnAsJson ? await response.json() : await response.text();
    } else if (response.status === 400) {
        const result = await response.json();
        throw new ValidationError(result.errors);
    } else {
        throw new ApiError(response);
    }
}

export async function getToken() {
    if (tokenCache.token && tokenCache.expiresAt && Date.now() < tokenCache.expiresAt) {
        return tokenCache.token;
    }
    
    try {
        const response = await fetch('/api/Security/token');

        if (!response.ok) {
            console.error('error getting xsrf token', response.status, response.statusText);
            return 'ERROR_GETTING_XSRF_TOKEN_CHECK_LOGS';
        }

        const data = await response.json();
        const token = data.token;
        
        tokenCache.expiresAt = Date.now() + (1000 * 60 * 55); // expire in 55 minutes, the token itself is valid for 60 
        tokenCache.token = token;
        
        return token;
    } catch (e) {
        console.error('error getting xsrf token:', e);
        return 'ERROR_GETTING_XSRF_TOKEN_CHECK_LOGS';
    }
}

export function invalidateTokenCache() {
    tokenCache.token = null;
    tokenCache.expiresAt = null;
}

export async function getHeadersWithToken(headers = {}) {
    const token = await getToken();
    return {
        ...headers,
        'X-XSRF-TOKEN': token,
    };
}

export async function projectBoardsCreate({ name, segmentTypeIds, notes }) {
    const payload = {
        name: name,
        segmentTypeIds: segmentTypeIds,
        notes: notes
    };

    const response = await fetch('/api/ProjectBoards', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: await getHeadersWithToken({
            'Content-Type': 'application/json'
        })
    });
    
    return await handleResponse(response);
}

export async function projectBoardsList() {
    const url = new URL('/api/ProjectBoards', window.location.origin);
    url.searchParams.set('pageSize', 1000);

    const response = await fetch(url.toString());
    return await handleResponse(response);
}

export async function projectBoardsClone(id) {
    const response = await fetch(`/api/ProjectBoards/${id}/clone`, {
        method: 'POST',
        headers: await getHeadersWithToken({
            'Content-Type': 'application/json'
        })
    });
    
    return await handleResponse(response);
}

export async function projectBoardsDelete(id) {
    const response = await fetch(`/api/ProjectBoards/${id}`, {
        method: 'DELETE',
        headers: await getHeadersWithToken()
    });
    
    return await handleResponse(response);
}

export async function segmentTypesList() {
    const response = await fetch('/api/Segments');
    return await handleResponse(response);
}

export async function occupationTypesList() {
    const response = await fetch('/api/Occupations');
    return await handleResponse(response);
}

export async function projectBoardsGet(id) {
    const response = await fetch(`/api/ProjectBoards/${id}`);
    return await handleResponse(response);
}

export async function projectBoardsGetShared(id, sharedInfo) {
    const response = await fetch(`/api/ProjectBoards/${id}/shared/${sharedInfo}`);
    return await handleResponse(response);
}

export async function projectBoardsGenerateSharedUrl(id) {
    const response = await fetch(`/api/ProjectBoards/${id}/share`)
    return await handleResponse(response, false);
}

export async function projectBoardsGetUploadSettings() {
    const response = await fetch('/api/ProjectBoards/items/upload-settings');
    return await handleResponse(response);
}

export async function projectBoardsUpdate({ id, name, segmentTypeIds, notes }) {
    const payload = { name, segmentTypeIds, notes };
    const response = await fetch(`/api/ProjectBoards/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
        headers: await getHeadersWithToken({
            'Content-Type': 'application/json'
        })
    });
    return await handleResponse(response);
}

export async function projectBoardItemsGet(boardId) {
    const res = await fetch(`/api/ProjectBoards/${boardId}/items`);
    if (!res.ok) throw new Error("Failed to fetch project board items");
    return await res.json();
}

export async function projectBoardItemsGetShared(boardId, sharedInfo) {
    const res = await fetch(`/api/ProjectBoards/${boardId}/shared/${sharedInfo}/items`);
    if (!res.ok) throw new Error("Failed to fetch project board items");
    return await res.json();
}

export async function projectBoardItemDelete(boardId, itemId) {
    const res = await fetch(`/api/ProjectBoards/${boardId}/items/${itemId}`, {
        method: "DELETE",
        headers: await getHeadersWithToken()
    });
    if (!res.ok) throw new Error("Failed to delete project board item");
    return true;
}

export async function projectBoardItemGet(boardId, itemId, sharedInfo = null) {
    const basePath = sharedInfo
        ? `/api/ProjectBoards/${boardId}/shared/${sharedInfo}/items/${itemId}`
        : `/api/ProjectBoards/${boardId}/items/${itemId}`;

    const res = await fetch(basePath);
    if (!res.ok) throw new Error("Failed to fetch project board item");
    return await res.json();
}

export async function projectBoardItemUpdate(boardId, itemId, { notes }) {
    const response = await fetch(`/api/ProjectBoards/${boardId}/items/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify({ notes }),
        headers: await getHeadersWithToken({
            'Content-Type': 'application/json'
        })
    });

    return await handleResponse(response, false);
}

export async function projectBoardSetCoverImage(boardId, itemId) {
    const response = await fetch(`/api/ProjectBoards/${boardId}/coverImage/${itemId}`, {
        method: 'POST',
        headers: await getHeadersWithToken()
    });

    return await handleResponse(response, false);
}

export async function projectBoardItemsAdd(boardIds, assetInfo) {
    const results = [];

    for (const boardId of boardIds) {
        try {
            const res = await fetch(`/api/ProjectBoards/${boardId}/items`, {
                method: "POST",
                headers: await getHeadersWithToken({ "Content-Type": "application/json" }),
                body: JSON.stringify({ assetInfo })
            });

            if (!res.ok) {
                const text = await res.text();
                results.push({ boardId, success: false, error: text });
            } else {
                results.push({ boardId, success: true });
            }
        } catch (err) {
            results.push({ boardId, success: false, error: err.message });
        }
    }

    return results;
}

export async function navigationGetHeader(currentPage = null) {
    const res = await fetch('/api/Navigation/Header?currentPage=' + currentPage);
    return await handleResponse(res, false);
}

export async function newsletterSubscribe(email) {
    const res = await fetch('/api/Newsletter', {
        method: 'POST',
        headers: await getHeadersWithToken({
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(email)
    });
    return await handleResponse(res);
}

export async function forgotPassword(email) {
    const res = await fetch('/api/Accounts/ForgotPassword', {
        method: 'POST',
        headers: await getHeadersWithToken({
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify({username: email})
    });
    return await handleResponse(res);
}

